import PropTypes from 'prop-types';
import dateFormat from 'dateformat';
import { get, flatten } from 'lodash-es';
import sha1 from 'hash.js/lib/hash/sha/1';
import { createModelSegment } from 'client/data/luckdragon/segment';
import { EdmundsGraphQLFederation } from 'client/data/graphql/graphql-client';
import { createDate, getCurrentDate } from 'site-modules/shared/utils/date-utils';
import { withMetrics } from 'client/data/api/api-metrics';
import { VisitorModel } from 'client/data/models/visitor';
import { PageModel } from 'client/data/models/page';
import { getQuery } from 'client/utils/location';
import {
  DEFAULT_FILTERS,
  DEFAULT_NAVIGATION_PARAMS,
  DISPLAY_MODE,
  ELECTRIC_CAR_INCENTIVES_GRAPHQL_QUERY,
  FILTER_QUERY_PARAMS,
  INCENTIVES_MMY,
  PAGE_SIZE,
  QUERIES,
  SORTING_CONFIG,
  SORTING_NAMES,
} from 'site-modules/shared/constants/incentives/electric-car-incentives';
import { isNode } from 'client/utils/environment';
import { getGqlIncentiveFilters, isDefaultValue } from 'site-modules/shared/utils/incentives/electric-car-incentives';
import { logger } from 'client/utils/logger/logger';

const SpecificIncentivesFilter = PropTypes.shape({
  pageNum: PropTypes.number,
  sortBy: PropTypes.string,
  makeSlug: PropTypes.string,
  modelSlug: PropTypes.string,
  programTypes: PropTypes.arrayOf(PropTypes.string),
  engineType: PropTypes.string,
  activeDate: PropTypes.string,
  transactionType: PropTypes.string,
  vehCondition: PropTypes.string,
});

const IncentivesModelYear = PropTypes.shape({
  id: PropTypes.string,
  makeName: PropTypes.string,
  modelName: PropTypes.string,
  makeSlug: PropTypes.string,
  modelSlug: PropTypes.string,
  year: PropTypes.number,
  publicationStates: PropTypes.arrayOf(PropTypes.string),
  segmentRatings: PropTypes.arrayOf(
    PropTypes.shape({
      rating: PropTypes.number,
    })
  ),
  styleList: PropTypes.shape({
    styleAggregations: PropTypes.shape({
      maxEdmundsElectricityRange: PropTypes.number,
      maxEPAElectricityRange: PropTypes.number,
      priceRange: PropTypes.shape({
        min: PropTypes.number,
        max: PropTypes.number,
      }),
    }),
  }),
});

const IncentiveData = PropTypes.shape({
  programType: PropTypes.string,
  subtype: PropTypes.string,
  subtypeId: PropTypes.number,
  rebateAmount: PropTypes.number,
  endDate: PropTypes.string,
  restrictions: PropTypes.string,
  id: PropTypes.string,
  type: PropTypes.string,
  transactionTypes: PropTypes.arrayOf(PropTypes.string),
  grantor: PropTypes.string,
  updateDate: PropTypes.string,
  rebateOverridesByEligibility: PropTypes.arrayOf(
    PropTypes.shape({
      rebateAmount: PropTypes.number,
    })
  ),
});

const IncentivesByModelYearData = PropTypes.shape({
  modelYear: IncentivesModelYear,
  incentives: PropTypes.arrayOf(IncentiveData),
  maxCalcNonChargingPercent: PropTypes.number,
  maxCalcIncentiveSum: PropTypes.number,
  maxCalcChargingSum: PropTypes.number,
  maxCalcNonChargingSum: PropTypes.number,
  maxCalcIncentiveStyleId: PropTypes.string,
});

const IncentivesAggregationData = PropTypes.shape({
  modelYearCount: PropTypes.number,
  makes: PropTypes.arrayOf(PropTypes.string),
  models: PropTypes.arrayOf(PropTypes.string),
  engineTypes: PropTypes.arrayOf(PropTypes.string),
  programTypes: PropTypes.arrayOf(PropTypes.string),
  byModelYear: PropTypes.arrayOf(IncentivesByModelYearData),
});

const IncentivesData = PropTypes.shape({
  incentivesOrdered: PropTypes.arrayOf(IncentiveData),
  incentivesCount: PropTypes.number,
});

export const IncentivesElectricEntities = {
  SpecificIncentivesFilter,
  IncentivesModelYear,
  IncentivesByModelYearData,
  IncentivesAggregationData,
  IncentiveData,
  IncentivesData,
};

const getIncentivesAggregationsPath = () => 'incentivesList.incentiveAggregations';
const getIncentivesAggregationsMMYPath = ({ makeSlug, modelSlug, year, styleId }) =>
  `incentivesList.incentiveAggregationsMMY.make["${makeSlug}"].model["${modelSlug}"].year["${year}"].styleId["${styleId}"]`;
const getIncentivesPath = () => 'incentivesList.incentives';
const getIncentivesGuidePath = ({ makeSlug, modelSlug }) =>
  `incentivesList.incentivesGuide.make["${makeSlug}"].model["${modelSlug}"]`;
const getFilterPath = () => 'incentivesFilter';
const getDefaultActiveDatePath = () => 'defaultActiveDate';
const getSpecificFilterPath = tab => `specificIncentiveFilter.tab["${tab}"]`;

export const IncentivesElectricPaths = {
  getDefaultActiveDatePath,
  getFilterPath,
  getSpecificFilterPath,
  getIncentivesAggregationsPath,
  getIncentivesAggregationsMMYPath,
  getIncentivesPath,
  getIncentivesGuidePath,
};

export const getElectricIncentivesData = (context, segmentName, variables) =>
  withMetrics(EdmundsGraphQLFederation, context).query(ELECTRIC_CAR_INCENTIVES_GRAPHQL_QUERY[segmentName], {
    ...variables,
  });

const getHash = incentivesFilter => {
  const stringFilter = Object.values(incentivesFilter).reduce((string, filterValue) => {
    if (Array.isArray(filterValue)) {
      return string.concat(filterValue.join('|'));
    }
    return string.concat(filterValue);
  }, '');

  return sha1().update(stringFilter).digest('hex');
};

const getIncentivesFilter = ({
  makeSlug,
  modelSlug,
  programTypes,
  engineType,
  activeDate,
  defaultActiveDate,
  transactionType,
  vehCondition,
  zip,
  types,
}) => ({
  makeSlug: makeSlug || DEFAULT_FILTERS[FILTER_QUERY_PARAMS.MAKE],
  modelSlug: modelSlug || DEFAULT_FILTERS[FILTER_QUERY_PARAMS.MODEL],
  programTypes: programTypes || DEFAULT_FILTERS[FILTER_QUERY_PARAMS.PROGRAM_TYPE],
  engineType: engineType || DEFAULT_FILTERS[FILTER_QUERY_PARAMS.ENGINE_TYPE],
  activeDate: activeDate || defaultActiveDate,
  transactionType: transactionType || DEFAULT_FILTERS[FILTER_QUERY_PARAMS.TRANSACTION_TYPE],
  vehCondition: vehCondition || DEFAULT_FILTERS[FILTER_QUERY_PARAMS.VEHICLE_CONDITION],
  types: types || ['CUSTOMER_APR', 'DEALER_CASH'],
  defaultActiveDate,
  zip,
});

const propagatePreExistingFilters = (existingValues, responseValues) =>
  Array.from(new Set(flatten([responseValues, isDefaultValue(existingValues) ? [] : existingValues])));

export const IncentivesElectricModel = createModelSegment('incentivesElectric', [
  {
    path: 'defaultActiveDate',
    async resolve(match, context) {
      if (!isNode()) {
        return dateFormat(createDate(getCurrentDate()), 'yyyy-mm-dd');
      }

      const visitorLocation = await context.resolveValue('location', VisitorModel);

      // defaulting to 0 so that the math below doesn't return NAN if gmtOffset is undefined or null.
      const gmtOffset = get(visitorLocation, 'gmtOffset', 0);

      return dateFormat(createDate(getCurrentDate().getTime() + gmtOffset * 60 * 60 * 1000), 'yyyy-mm-dd');
    },
  },

  /**
   * The path contains only filters that pass to graphQL call
   * @see getFilterPath
   */
  {
    path: 'incentivesFilter',
    async resolve(match, context) {
      const [location, zip, defaultActiveDate] = await Promise.all([
        context.resolveValue('location', PageModel),
        context.resolveValue('location.zipCode', VisitorModel),
        context.resolveValue(getDefaultActiveDatePath(), IncentivesElectricModel, false),
      ]);

      const {
        [FILTER_QUERY_PARAMS.MAKE]: makeSlug,
        [FILTER_QUERY_PARAMS.MODEL]: modelSlug,
        [FILTER_QUERY_PARAMS.PROGRAM_TYPE]: programTypesString,
        [FILTER_QUERY_PARAMS.ENGINE_TYPE]: engineType,
        [FILTER_QUERY_PARAMS.ACTIVE_DATE]: activeDate,
        [FILTER_QUERY_PARAMS.TRANSACTION_TYPE]: transactionType,
        [FILTER_QUERY_PARAMS.VEHICLE_CONDITION]: vehCondition,
      } = getQuery(location);

      return {
        incentivesFilter: getIncentivesFilter({
          makeSlug,
          modelSlug,
          programTypes: programTypesString?.split(','),
          engineType,
          activeDate,
          defaultActiveDate,
          transactionType,
          vehCondition,
          zip,
        }),
        eligibilityFactors: [],
        excludedFilters: {},
      };
    },
    async update(newValue, match, context) {
      const [defaultActiveDate, { incentivesFilter }] = await Promise.all([
        context.resolveValue(getDefaultActiveDatePath(), IncentivesElectricModel, false),
        context.resolveValue(getFilterPath(), IncentivesElectricModel, false),
      ]);

      const {
        incentivesFilter: newIncentivesFilter = {},
        eligibilityFactors = [],
        excludedFilters = {},
      } = newValue || {};

      return {
        incentivesFilter: getIncentivesFilter({
          ...incentivesFilter,
          ...newIncentivesFilter,
          defaultActiveDate,
        }),
        eligibilityFactors,
        excludedFilters,
      };
    },
  },
  /**
   * @see getSpecificFilterPath
   */
  {
    path: 'specificIncentiveFilter.tab["{tabName}"]',
    async resolve({ tabName }, context) {
      const [location, { incentivesFilter, eligibilityFactors }] = await Promise.all([
        context.resolveValue('location', PageModel),
        context.resolveValue(getFilterPath()),
      ]);

      const {
        [FILTER_QUERY_PARAMS.PAGENUM]: pageNum,
        [FILTER_QUERY_PARAMS.SORT_BY]: sortBy,
        [FILTER_QUERY_PARAMS.DISPLAY_MODE]: displayMode = DISPLAY_MODE.MODEL_YEARS,
      } = getQuery(location);

      const pageConfig =
        displayMode === tabName
          ? {
              pageNum: +pageNum || DEFAULT_NAVIGATION_PARAMS[tabName][FILTER_QUERY_PARAMS.PAGENUM],
              sortBy:
                (SORTING_CONFIG[tabName][sortBy] && sortBy) ||
                DEFAULT_NAVIGATION_PARAMS[tabName][FILTER_QUERY_PARAMS.SORT_BY],
            }
          : {
              pageNum: DEFAULT_NAVIGATION_PARAMS[tabName][FILTER_QUERY_PARAMS.PAGENUM],
              sortBy: DEFAULT_NAVIGATION_PARAMS[tabName][FILTER_QUERY_PARAMS.SORT_BY],
            };

      return { incentivesFilter: { ...pageConfig, ...incentivesFilter }, eligibilityFactors };
    },
    async update(newValue, { tabName }, context) {
      const [
        { incentivesFilter, eligibilityFactors, excludedFilters },
        { incentivesFilter: specificIncentivesFilter },
      ] = await Promise.all([
        context.resolveValue(getFilterPath()),
        context.resolveValue(getSpecificFilterPath(tabName)),
      ]);

      return newValue
        ? { incentivesFilter: { ...specificIncentivesFilter, ...incentivesFilter, ...newValue }, eligibilityFactors }
        : {
            incentivesFilter: {
              ...incentivesFilter,
              pageNum: DEFAULT_NAVIGATION_PARAMS[tabName][FILTER_QUERY_PARAMS.PAGENUM],
              sortBy: DEFAULT_NAVIGATION_PARAMS[tabName][FILTER_QUERY_PARAMS.SORT_BY],
            },
            eligibilityFactors,
            excludedFilters,
          };
    },
  },
  {
    /**
     * @see getIncentivesAggregationsPath
     */
    path: 'incentivesList.incentiveAggregations',
    async resolve(match, context) {
      const { incentivesFilter, eligibilityFactors, excludedFilters } = await context.resolveValue(
        getSpecificFilterPath(DISPLAY_MODE.MODEL_YEARS)
      );

      const { pageNum, sortBy, ...restIncentivesFilter } = incentivesFilter;
      const incentiveFilters = getGqlIncentiveFilters(restIncentivesFilter, excludedFilters);

      try {
        const incentiveAggregationsResponse = await getElectricIncentivesData(context, QUERIES.INCENTIVE_AGGREGATIONS, {
          incentiveFilters,
          eligibilityFactors,
          page: { pageNum, pageSize: PAGE_SIZE[DISPLAY_MODE.MODEL_YEARS] },
          sortBy: SORTING_CONFIG[DISPLAY_MODE.MODEL_YEARS][sortBy],
        });

        const { byModelYear, ...restIncentiveAggregations } = get(
          incentiveAggregationsResponse,
          'incentives.incentiveAggregations',
          {}
        );

        if (!byModelYear) {
          return { byModelYear: null };
        }

        const hash = getHash(incentivesFilter);

        return {
          ...restIncentiveAggregations,
          makes: propagatePreExistingFilters(incentivesFilter.makeSlug, restIncentiveAggregations.makes),
          models: propagatePreExistingFilters(incentivesFilter.modelSlug, restIncentiveAggregations.models),
          programTypes: propagatePreExistingFilters(
            incentivesFilter.programTypes,
            restIncentiveAggregations.programTypes
          ),
          byModelYear: byModelYear.map(byModelYearItem => ({ ...byModelYearItem, hash })),
        };
      } catch (e) {
        logger.warn('Failed to load data', e);
        return { byModelYear: null };
      }
    },
  },
  {
    /**
     * @see getIncentivesAggregationsMMYPath
     */
    path: 'incentivesList.incentiveAggregationsMMY.make["{makeSlug}"].model["{modelSlug}"].year["{year}"].styleId["{styleId}"]',
    async resolve({ makeSlug, modelSlug, year, styleId }, context) {
      const { incentivesFilter, eligibilityFactors, excludedFilters } = await context.resolveValue(
        getSpecificFilterPath(DISPLAY_MODE.MODEL_YEARS)
      );

      const incentiveFilters = getGqlIncentiveFilters(
        {
          ...incentivesFilter,
          makeSlug,
          modelSlug,
          year,
          styleIds: [styleId],
        },
        excludedFilters
      );

      const incentiveAggregationsResponse = await getElectricIncentivesData(context, QUERIES.INCENTIVES, {
        incentiveFilters,
        eligibilityFactors,
        page: { pageNum: 1, pageSize: PAGE_SIZE[INCENTIVES_MMY] },
        sortBy: SORTING_CONFIG[INCENTIVES_MMY][SORTING_NAMES.AMOUNT],
      });

      return get(incentiveAggregationsResponse, 'incentives.incentiveAggregations.byGrantorAndSubtype', []).map(
        incentives => get(incentives, 'topIncentiveByRebate')
      );
    },
  },
  {
    /**
     * @see getIncentivesPath
     */
    path: 'incentivesList.incentives',
    async resolve(match, context) {
      const { incentivesFilter, eligibilityFactors, excludedFilters } = await context.resolveValue(
        getSpecificFilterPath(DISPLAY_MODE.INCENTIVES)
      );

      const { pageNum, sortBy, ...restIncentivesFilter } = incentivesFilter;
      const incentiveFilters = getGqlIncentiveFilters(restIncentivesFilter, excludedFilters);

      try {
        const incentivesResponse = await getElectricIncentivesData(context, QUERIES.INCENTIVES, {
          incentiveFilters,
          eligibilityFactors,
          page: { pageNum, pageSize: PAGE_SIZE[DISPLAY_MODE.INCENTIVES] },
          sortBy: SORTING_CONFIG[DISPLAY_MODE.INCENTIVES][sortBy],
        });

        const byGrantorAndSubtypeIncentives = get(
          incentivesResponse,
          'incentives.incentiveAggregations.byGrantorAndSubtype'
        );

        if (!byGrantorAndSubtypeIncentives) {
          return { incentivesOrdered: null, incentivesCount: 0 };
        }

        const hash = getHash(incentivesFilter);

        const incentivesOrdered = byGrantorAndSubtypeIncentives
          .map(incentives => get(incentives, 'topIncentiveByRebate'))
          .map(incentive => ({ ...incentive, hash }));
        const incentivesCount = get(incentivesResponse, 'incentives.incentiveAggregations.grantorAndSubtypeCount', 0);

        return { incentivesOrdered, incentivesCount };
      } catch (e) {
        logger.warn('Failed to load data', e);
        return { incentivesOrdered: null, incentivesCount: 0 };
      }
    },
  },
  {
    /**
     * @see getIncentivesGuidePath
     */
    path: 'incentivesList.incentivesGuide.make["{makeSlug}"].model["{modelSlug}"]',
    async resolve({ makeSlug, modelSlug }, context) {
      const [zip, activeDate] = await Promise.all([
        context.resolveValue('location.zipCode', VisitorModel),
        context.resolveValue(getDefaultActiveDatePath(), IncentivesElectricModel, false),
      ]);
      const incentiveFilters = getGqlIncentiveFilters({
        makeSlug,
        modelSlug,
        transactionType: 'FINANCE',
        vehCondition: 'NEW',
        engineType: 'electric',
        activeDate,
        types: [],
        zip,
      });
      const incentivesResponse = await getElectricIncentivesData(context, QUERIES.INCENTIVES, {
        incentiveFilters,
        page: { pageNum: 1, pageSize: 4 },
        sortBy: { topIncentiveByRebateAmount: 'DESC' },
      });

      const incentivesOrdered = get(incentivesResponse, 'incentives.incentiveAggregations.byGrantorAndSubtype', []).map(
        incentives => get(incentives, 'topIncentiveByRebate')
      );
      const incentivesCount = get(incentivesResponse, 'incentives.incentiveAggregations.grantorAndSubtypeCount', 0);

      return { incentivesOrdered, incentivesCount };
    },
  },
]);
